import React, { useState } from 'react';
import {
  Alert,
  Button,
  Content,
  Flex,
  FlexItem,
  Label,
  LabelGroup,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import type {
  VerdaProductCreateRequest,
  VerdaProductCreateResponse,
  VerdaProductCreateResult,
} from '../../../types/models/Verda';
import type { ApiResponseVerdaProductCreate } from '../../../types/api/ApiResponseVerda';
import { formatCurrencyAmount, formatUnitPrice } from '../../../utils/money';

/** What the staff member asked for: the selected rows, or every type not yet linked. */
export type VerdaCreateProductsRequest =
  | { kind: 'selected'; codes: string[] }
  /** `missingCodes` is what the tab sees as unlinked, for the confirmation list only; the POST sends `all`. */
  | { kind: 'all'; missingCodes: string[] };

interface VerdaCreateProductsModalProps {
  request: VerdaCreateProductsRequest;
  onClose: () => void;
  /** Called after a successful POST (whatever the per-type outcome), so the tab can refresh its links. */
  onCreated: (response: VerdaProductCreateResponse) => void;
}

const STATUS_LABEL: Record<VerdaProductCreateResult['status'], { text: string; color: 'green' | 'grey' | 'red' }> = {
  created: { text: 'Created', color: 'green' },
  exists: { text: 'Already existed', color: 'grey' },
  failed: { text: 'Failed', color: 'red' },
};

/**
 * Confirm, then show the outcome of, `POST /admin/verda/products` (docs/api-types/admin-verda.md). The
 * backend owns every rule — which codes are valid, the price (Verda's on-demand EUR price × 1.20,
 * rounded up to 4 decimals), the key, the French label — and the call is idempotent: an already
 * linked type comes back `exists` and is left untouched, so "Create all missing" is safe to repeat.
 * Mounted only while open (keyed upstream), so each run starts on the confirmation step.
 */
function VerdaCreateProductsModal({ request, onClose, onCreated }: VerdaCreateProductsModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<VerdaProductCreateResponse | null>(null);

  const codes = request.kind === 'selected' ? request.codes : request.missingCodes;
  const title =
    request.kind === 'selected'
      ? `Create ${codes.length} product${codes.length === 1 ? '' : 's'}`
      : 'Create all missing products';

  const handleConfirm = async () => {
    const body: VerdaProductCreateRequest =
      request.kind === 'selected' ? { instance_types: request.codes } : { all: true };
    setIsSubmitting(true);
    setError(null);
    try {
      const res = await apiFetch('/admin/verda/products', { method: 'POST', body: JSON.stringify(body) });
      // Branch on the JSend body.status, NEVER response.ok (errors arrive at HTTP 200).
      const json = (await res.json().catch(() => null)) as ApiResponseVerdaProductCreate | null;
      const data = json?.data;
      if (json?.status === 'success' && data?.results && data.summary && data.currency && data.markup_percent) {
        const result: VerdaProductCreateResponse = {
          results: data.results,
          summary: data.summary,
          currency: data.currency,
          markup_percent: data.markup_percent,
        };
        setResponse(result);
        onCreated(result);
        const { created, exists, failed } = result.summary;
        addNotification({
          title: failed > 0 ? 'Products created with failures' : 'Products created',
          srTitle: failed > 0 ? 'Warning alert' : 'Success alert',
          description: `${created} created, ${exists} already existed, ${failed} failed.`,
          variant: failed > 0 ? 'warning' : 'success',
        });
        setDrawerOpen(true);
        return;
      }
      const message = json?.message ?? `Could not create the products (HTTP ${res.status}).`;
      setError(message);
      addNotification({
        title: 'Could not create products',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setError(message);
      addNotification({
        title: 'Could not create products',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderConfirm = () => (
    <>
      {error && (
        <Alert variant="danger" isInline title="Could not create the products" className="pf-v6-u-mb-md">
          {error}
        </Alert>
      )}
      <Content component="p">
        {request.kind === 'selected' ? (
          <>
            A catalog product will be created for each of these <strong>{codes.length}</strong> Verda instance types.
          </>
        ) : (
          <>
            A catalog product will be created for <strong>every</strong> instance type in Verda&apos;s live catalog that
            doesn&apos;t have one yet — <strong>{codes.length}</strong> as of the last fetch.
          </>
        )}
      </Content>
      <Content component="ul">
        <Content component="li">Priced at Verda&apos;s on-demand EUR price × 1.20, rounded up to 4 decimals.</Content>
        <Content component="li">Billed hourly, in the “GPU Server” category.</Content>
        <Content component="li">Active, and hidden from customers until you make it visible.</Content>
        <Content component="li">Instance types that already have a product are left unchanged.</Content>
      </Content>
      {codes.length > 0 && (
        <LabelGroup numLabels={24} isCompact aria-label="Instance types to create products for">
          {codes.map((code) => (
            <Label key={code} isCompact>
              <span className="pf-v6-u-font-family-monospace">{code}</span>
            </Label>
          ))}
        </LabelGroup>
      )}
    </>
  );

  const renderResult = (result: VerdaProductCreateResponse) => (
    <>
      <Flex spaceItems={{ default: 'spaceItemsSm' }} className="pf-v6-u-mb-md">
        <FlexItem>
          <Label color="green">{result.summary.created} created</Label>
        </FlexItem>
        <FlexItem>
          <Label color="grey">{result.summary.exists} already existed</Label>
        </FlexItem>
        <FlexItem>
          <Label color={result.summary.failed > 0 ? 'red' : 'grey'}>{result.summary.failed} failed</Label>
        </FlexItem>
      </Flex>
      {result.results.length > 0 && (
        <Table aria-label="Product creation results" variant="compact">
          <Thead>
            <Tr>
              <Th>Instance type</Th>
              <Th>Result</Th>
              <Th>Product</Th>
              <Th>Verda cost / h</Th>
              <Th>Sale price / h</Th>
            </Tr>
          </Thead>
          <Tbody>
            {result.results.map((row) => (
              <Tr key={row.instance_type}>
                <Td dataLabel="Instance type">
                  <span className="pf-v6-u-font-family-monospace">{row.instance_type}</span>
                </Td>
                <Td dataLabel="Result">
                  <Label isCompact color={STATUS_LABEL[row.status].color}>
                    {STATUS_LABEL[row.status].text}
                  </Label>
                  {row.status === 'failed' && (
                    <div>
                      <Content component="small">{row.reason}</Content>
                    </div>
                  )}
                </Td>
                <Td dataLabel="Product">
                  {row.status === 'failed' ? (
                    '—'
                  ) : (
                    <span className="pf-v6-u-font-family-monospace">{row.product.product_key}</span>
                  )}
                </Td>
                <Td dataLabel="Verda cost / h" modifier="nowrap">
                  {row.status === 'created' ? formatCurrencyAmount(row.cost_per_hour, result.currency) : '—'}
                </Td>
                <Td dataLabel="Sale price / h" modifier="nowrap">
                  {row.status === 'created'
                    ? formatCurrencyAmount(row.sale_price_per_hour, result.currency)
                    : row.status === 'exists' && row.product.default_unit_price !== null
                      ? formatUnitPrice(row.product.default_unit_price)
                      : '—'}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </>
  );

  return (
    <Modal
      variant={response ? 'medium' : 'small'}
      isOpen
      onClose={isSubmitting ? undefined : onClose}
      aria-labelledby="verda-create-products-title"
    >
      <ModalHeader
        title={response ? 'Product creation results' : title}
        labelId="verda-create-products-title"
        description={response ? undefined : 'From the Verda instance-type catalog'}
      />
      <ModalBody>{response ? renderResult(response) : renderConfirm()}</ModalBody>
      <ModalFooter>
        {response ? (
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button
              variant="primary"
              onClick={() => void handleConfirm()}
              isLoading={isSubmitting}
              isDisabled={isSubmitting || codes.length === 0}
            >
              {title}
            </Button>
            <Button variant="link" onClick={onClose} isDisabled={isSubmitting}>
              Cancel
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}

export default VerdaCreateProductsModal;
